import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import axios from "../../api/axios";
import { PreferencesContext } from "../../context/PreferencesContext";
import { AuthContext } from "../../context/AuthContext";

import StudentDashboard from "../../components/reports/StudentDashboard";

export default function StudentDashboardPage() {
  const { organization, academicYear } = useContext(PreferencesContext);
  const { user } = useContext(AuthContext);

  const [branches, setBranches] = useState([]);
  const [grades, setGrades] = useState([]);
  const [filters, setFilters] = useState({
    branchIds: [],
    grade: "",
    academicYear: "",
    saleType: "books",
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organization) return;

    // Fetch branches (respect role)
    axios.get(`/branches?orgId=${organization}`, {}).then((res) => {
      let list = res.data || [];
      if (user?.role === "branch_manager") {
        list = list.filter((b) => user.branches.includes(b._id));
      }
      setBranches(list);
      if (list.length === 1) {
        setFilters((prev) => ({ ...prev, branchIds: [list[0]._id] }));
      }
    });

    // Fetch grades
    axios.get(`/masters/${organization}`, {}).then((res) => {
      setGrades(res.data.grades || []);
    });
  }, [organization]);

  useEffect(() => {
    if (organization && academicYear) {
      setFilters((prev) => ({ ...prev, academicYear }));
      setLoading(false);
    }
  }, [organization, academicYear]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const allFilters = useMemo(
    () => ({ orgId: organization, academicYear: academicYear, ...filters }),
    [organization, academicYear, filters]
  );

  if (loading) return <CircularProgress />;

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Student Purchase Coverage
      </Typography>

      {/* 🔍 Filters */}
      <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Sale type</InputLabel>
          <Select
            label="Sale type"
            value={filters.saleType}
            onChange={(e) => handleFilterChange("saleType", e.target.value)}
          >
            <MenuItem value="books">Books only</MenuItem>
            <MenuItem value="uniforms">Uniforms only</MenuItem>
            <MenuItem value="all">All sales</MenuItem>
          </Select>
        </FormControl>

        {branches.length > 1 && (
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Branch</InputLabel>
            <Select
              label="Branch"
              multiple
              value={filters.branchIds}
              onChange={(e) => handleFilterChange("branchIds", e.target.value)}
            >
              {branches.map((b) => (
                <MenuItem key={b._id} value={b._id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Grade</InputLabel>
          <Select
            label="Grade"
            value={filters.grade}
            onChange={(e) => handleFilterChange("grade", e.target.value)}
          >
            <MenuItem value="">All Grades</MenuItem>
            {grades.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* 📊 Dashboard */}
      <StudentDashboard filters={allFilters} saleType={filters.saleType} />
    </Box>
  );
}
